import { Tooltip } from 'antd';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  CpBody01Placeholder,
  CpBody02Placeholder,
} from 'components/atoms/customer-portal/typography';
import { useGetBackground, useGetStyle } from '../../types/utils';
import { CpWidgetProps } from '../../types/widget';
import { CpSectionWrapper, PaddingSize } from '../common';
import { CpWidgetHeader } from '../common/header';
import {
  SummaryCardInfoCardsWrapper,
  SummaryInfoCardWrapper,
  SummaryInfoCard,
  SummaryInfoCardLabel,
  SummaryInfoCardValue as SummaryInfoCardValueBase,
} from '../cp-summary-information/cp-summary-information';

interface CpProjectHoursData extends Record<string, unknown> {
  title: string;
  description: string;
  trackedMinutes: number;
  budgetedMinutes: number;
  remainingMinutes: number;
  trackedPercentage: number;
  remainingPercentage: number;
}

const SummaryInfoCardValue = styled(SummaryInfoCardValueBase)`
  display: flex;
  align-items: baseline;
  justify-content: start;

  /* overflow ellipsps */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${CpBody02Placeholder}, ${CpBody01Placeholder} {
    margin-left: var(--cp-spacing-03);
  }
`;

const formatMinutes = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  return `${hours}h ${remainingMinutes < 10 ? '0' : ''}${remainingMinutes}m`;
};

const ProgressWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: var(--cp-spacing-09);
`;

const ProgressBarBg = styled.div`
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: start;
  width: 100%;
  background: var(--cp-background-as-white-opacity);
  .cp-light-background &,
  .cp-very-light-background & {
    background: var(--cp-background-as-black-opacity);
  }
`;

const CompletedProgressBar = styled.div<{ percentage: number | string }>`
  height: 100%;
  background: var(--cp-green-40);
  width: ${({ percentage }) => percentage}%;
`;

const ProgressIndicatorWrapprer = styled.div<{ percentage: number | string }>`
  position: absolute;
  top: 50%;
  left: ${({ percentage }) => percentage}%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProjectHoursProgressBar: React.FC<{
  percentage: number | string;
}> = ({ percentage }) => {
  return (
    <ProgressWrapper>
      <ProgressBarBg>
        <CompletedProgressBar
          data-cy="cp.project-hours.progress-bar.completed"
          percentage={percentage}
        />
      </ProgressBarBg>
      <ProgressIndicatorWrapprer
        data-cy="cp.project-hours.progress-bar.indicator"
        percentage={percentage}
      >
        {percentage ? (
          <CpBody02Placeholder data-cy="cp.project-hours.progress-bar.percentage">
            {percentage}%
          </CpBody02Placeholder>
        ) : (
          <></>
        )}
      </ProgressIndicatorWrapprer>
    </ProgressWrapper>
  );
};

export const CpProjectHours: React.FC<CpWidgetProps> = ({
  widget,
  data: _data,
  disabled,
}) => {
  const data = useMemo(() => _data as CpProjectHoursData, [_data]);

  const background = useGetBackground(widget);
  const paddingY = useGetStyle(widget, 'paddingY', PaddingSize.Medium);

  // format should be x hh y mm
  // if y is 0, then it should be x hh 0y mm
  const trackedMinutesInHours = useMemo(() => {
    if (!data?.trackedMinutes) {
      return '-';
    }

    return formatMinutes(data.trackedMinutes);
  }, [data.trackedMinutes]);

  const budgetedMinutesInHours = useMemo(() => {
    if (!data.budgetedMinutes) {
      return '-';
    }

    return formatMinutes(data.budgetedMinutes);
  }, [data.budgetedMinutes]);

  const remainingMinutesInHours = useMemo(() => {
    if (data?.remainingMinutes < 0) {
      return '0h 0m';
    }

    if (!data.remainingMinutes) {
      return '-';
    }

    return formatMinutes(data.remainingMinutes);
  }, [data.remainingMinutes]);

  const trackedPercentage = useMemo(() => {
    // dont show after decimal if it is 0

    if (!data.trackedPercentage?.toString?.()?.includes?.('.')) {
      return data.trackedPercentage;
    }

    return data.trackedPercentage?.toFixed?.(1);
  }, [data.trackedPercentage]);

  const remainingPercentage = useMemo(() => {
    // dont show after decimal if it is 0

    if (data?.remainingPercentage < 0) {
      return 0;
    }

    if (!data?.remainingPercentage?.toString?.().includes?.('.')) {
      return data?.remainingPercentage;
    }

    return data?.remainingPercentage?.toFixed?.(1);
  }, [data?.remainingPercentage]);

  return (
    <CpSectionWrapper
      background={background}
      paddingY={paddingY}
      isDisabled={disabled}
    >
      <CpWidgetHeader>
        <CpWidgetHeader.Title data-cy="cp.project-hours.title" autoAdjustColor>
          {data.title}
        </CpWidgetHeader.Title>
        <CpWidgetHeader.Description
          data-cy="cp.project-hours.description"
          autoAdjustColor
        >
          {data.description}
        </CpWidgetHeader.Description>
      </CpWidgetHeader>

      {data?.trackedPercentage ? (
        <ProjectHoursProgressBar percentage={trackedPercentage} />
      ) : (
        <> </>
      )}

      <SummaryCardInfoCardsWrapper>
        <SummaryInfoCardWrapper>
          <SummaryInfoCard data-cy="cp.project-hours.summary-info-card.tracked-hours">
            <SummaryInfoCardLabel>
              <FormattedMessage id="projects.list.tracked_hours" />
            </SummaryInfoCardLabel>

            <Tooltip
              title={`${trackedMinutesInHours}${
                data?.trackedPercentage ? ` (${trackedPercentage}%)` : ''
              }`}
              placement="topLeft"
            >
              <SummaryInfoCardValue data-cy="cp.project-hours.summary-info-card.tracked-hours-value">
                {trackedMinutesInHours}
                {data?.trackedPercentage ? (
                  <CpBody02Placeholder>
                    ({trackedPercentage}%)
                  </CpBody02Placeholder>
                ) : (
                  <></>
                )}
              </SummaryInfoCardValue>
            </Tooltip>
          </SummaryInfoCard>
        </SummaryInfoCardWrapper>

        <SummaryInfoCardWrapper>
          <SummaryInfoCard data-cy="cp.project-hours.summary-info-card.remaining-hours">
            <SummaryInfoCardLabel>
              <FormattedMessage id="projects.list.remaining_hours" />
            </SummaryInfoCardLabel>

            <Tooltip
              title={`${remainingMinutesInHours}${
                remainingPercentage ? ` (${remainingPercentage}%)` : ''
              }`}
              placement="topLeft"
            >
              <SummaryInfoCardValue data-cy="cp.project-hours.summary-info-card.remaining-hours-value">
                {remainingMinutesInHours}
                {remainingPercentage ? (
                  <CpBody01Placeholder>
                    ({remainingPercentage}%)
                  </CpBody01Placeholder>
                ) : (
                  <></>
                )}
              </SummaryInfoCardValue>
            </Tooltip>
          </SummaryInfoCard>
        </SummaryInfoCardWrapper>

        <SummaryInfoCardWrapper>
          <SummaryInfoCard data-cy="cp.project-hours.summary-info-card.budgeted-hours">
            <SummaryInfoCardLabel>
              <FormattedMessage id="projects.list.budgeted_hours" />
            </SummaryInfoCardLabel>

            <Tooltip title={budgetedMinutesInHours} placement="topLeft">
              <SummaryInfoCardValue data-cy="cp.project-hours.summary-info-card.budgeted-hours-value">
                {budgetedMinutesInHours}
              </SummaryInfoCardValue>
            </Tooltip>
          </SummaryInfoCard>
        </SummaryInfoCardWrapper>
      </SummaryCardInfoCardsWrapper>
    </CpSectionWrapper>
  );
};
